import React, { useState } from 'react';
import { MapPin, X, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface EnableLocationSheetProps {
  onClose: (result: boolean) => void;
}

const EnableLocationSheet: React.FC<EnableLocationSheetProps> = ({ onClose }) => {
  const { updateUser } = useAuth();
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string>('');

  const explainGeoError = (e: GeolocationPositionError | Error): string => {
    // Browser GeolocationPositionError codes (where available)
    // 1: PERMISSION_DENIED, 2: POSITION_UNAVAILABLE, 3: TIMEOUT
    // Some browsers only provide message.
    // Also add guidance for insecure contexts.
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      return 'Location requires HTTPS. Please use https://vybetribe.app';
    }

    // @ts-expect-error older types
    const code = (e as GeolocationPositionError)?.code;
    if (code === 1) return 'Location permission was blocked. To fix:\n\n1. Tap the menu icon (⋮) in your browser\n2. Tap "Site settings" or "Permissions"\n3. Find "Location" and set to "Allow"\n4. Come back and try again';
    if (code === 2) return 'Cannot detect location. Check that:\n\n• Device Location is ON (Settings → Location)\n• You have internet connection\n• You are not in airplane mode\n\nThen try again.';
    if (code === 3) return 'Location timed out. This usually means:\n\n• Device Location is OFF\n• Poor GPS signal (try near a window)\n• Slow connection\n\nEnable device location and try again.';

    const msg = (e as Error)?.message || 'Could not get your location.';
    // Common iOS wording tweak
    if (/Only secure origins/i.test(msg)) {
      return 'Location requires HTTPS. Please use https://vybetribe.app';
    }
    return msg;
  };

  const handleEnable = async () => {
    if (isWorking) return;
    setIsWorking(true);
    setError('');

    try {
      if (!('geolocation' in navigator)) {
        throw new Error('Geolocation is not supported in this browser.');
      }
      if (!window.isSecureContext) {
        throw new Error('Location requires HTTPS. Please use https://vybetribe.app');
      }

      // Check if permission was already denied
      if ('permissions' in navigator) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          if (permissionStatus.state === 'denied') {
            throw new Error('Location permission was blocked. Tap the menu icon (⋮) → Site settings → Location → Allow, then try again.');
          }
        } catch (permError) {
          // Permission API not supported, continue with request
          console.log('Permission query not supported');
        }
      }

      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        });
      });

      const latitude = pos.coords.latitude;
      const longitude = pos.coords.longitude;

      // Keep minimal label to avoid blocking on reverse geocode
      const location = 'My area';

      await updateUser({ latitude, longitude, location });

      // ✅ resolve success so OnboardingCoach can advance
      onClose(true);
    } catch (e: any) {
      console.error('[location] failed:', e);
      setError(explainGeoError(e));
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-contrast flex items-center">
          <MapPin size={20} className="mr-2 text-secondary" />
          Enable Location
        </h2>
        <button
          className="text-contrast-500 hover:text-contrast p-1 rounded-full"
          onClick={() => onClose(false)}
        >
          <X size={20} />
        </button>
      </div>

      <p className="text-contrast-400 mb-4">
        Share your general location so we can show nearby Vybes.
      </p>

      {!error && (
        <div className="mb-4 bg-blue-500/10 border border-blue-500/20 p-3 rounded-md flex items-start">
          <AlertCircle size={18} className="mr-2 mt-0.5 text-blue-400 flex-shrink-0" />
          <div>
            <p className="text-blue-400 text-sm font-semibold mb-1">Watch for the browser prompt!</p>
            <p className="text-blue-300 text-xs">
              Your browser will show a permission request at the <span className="font-bold">top of the screen</span>. Tap <span className="font-bold">"Allow"</span> when you see it.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-secondary/10 p-3 rounded-md flex items-start">
          <AlertCircle size={18} className="mr-2 mt-0.5 text-secondary flex-shrink-0" />
          <p className="text-secondary text-sm whitespace-pre-line">{error}</p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <button
          onClick={handleEnable}
          disabled={isWorking}
          className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-3 rounded-full transition-colors disabled:opacity-50 font-semibold shadow-lg"
        >
          {isWorking ? 'Waiting for permission…' : 'Enable Location'}
        </button>
        <button
          onClick={() => onClose(false)}
          className="w-full bg-primary-100 text-contrast py-3 rounded-md hover:bg-primary-200 transition-colors"
        >
          Skip for Now
        </button>
      </div>
    </div>
  );
};

export default EnableLocationSheet;
